<template>
  <div
    class="staff-documents"
    data-cy="staff_documents_panel"
  >
    <div class="staff-documents__header">
      <h2 class="staff-documents__title">Documents</h2>

      <StandardButton
        @click="emit('upload')"
        data-cy="staff_documents_upload"
        prepend-icon="upload"
        text="Upload"
      />
    </div>

    <div
      class="staff-documents__tabs"
      role="tablist"
    >
      <button
        v-for="tab in tabs"
        @click="activeTab = tab.id"
        :key="tab.id"
        :aria-selected="activeTab === tab.id"
        :class="{ 'staff-documents__tab--active': activeTab === tab.id }"
        :data-cy="`staff_documents_tab_${tab.id}`"
        class="staff-documents__tab"
        role="tab"
        type="button"
      >
        {{ tab.label }} ({{ tab.count }})
      </button>
    </div>

    <label class="staff-documents__search">
      <svg
        aria-hidden="true"
        class="staff-documents__search-icon"
        fill="none"
        height="18"
        viewBox="0 0 18 18"
        width="18"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx="8"
          cy="8"
          r="5.25"
          stroke="currentColor"
          stroke-width="1.5"
        />
        <path
          d="m12.75 12.75 2.5 2.5"
          stroke="currentColor"
          stroke-linecap="round"
          stroke-width="1.5"
        />
      </svg>

      <input
        v-model="searchQuery"
        class="staff-documents__search-input"
        data-cy="staff_documents_search"
        placeholder="Search documents..."
        type="search"
      />
    </label>

    <div class="staff-documents__list">
      <div
        v-if="visibleRows.length === 0"
        class="staff-documents__empty"
      >
        No documents match your filters
      </div>

      <template v-else>
        <StaffDocumentRow
          v-for="(row, index) in visibleRows"
          @edit-expiry="emit('edit-expiry', $event)"
          @review-status="emit('review-status', $event)"
          :key="row.category"
          :can-review-documents="canReviewDocuments"
          :class="{ 'staff-documents__row--bordered': index > 0 }"
          :row="row"
          class="staff-documents__row"
        />
      </template>
    </div>
  </div>
</template>

<script setup>
import {
  STAFF_DOCUMENT_FILTER_TABS,
  buildStaffDocumentRows,
  countRowsByStatus,
  filterStaffDocumentRows,
} from '@/shared/utils/staffDocumentUtils';
import StaffDocumentRow from '@/shared/components/provider/staff-documents/StaffDocumentRow.vue';

const props = defineProps({
  attachments: {
    type: Array,
    default: () => [],
  },
  canReviewDocuments: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['edit-expiry', 'review-status', 'upload']);

const activeTab = ref('all');
const searchQuery = ref('');

const rows = computed(() => buildStaffDocumentRows(undefined, props.attachments));

const statusCounts = computed(() => countRowsByStatus(rows.value));

const tabs = computed(() =>
  STAFF_DOCUMENT_FILTER_TABS.map((tab) => ({
    ...tab,
    count: tab.id === 'all' ? rows.value.length : statusCounts.value[tab.label] || 0,
  })),
);

const visibleRows = computed(() =>
  filterStaffDocumentRows(rows.value, {
    activeTab: activeTab.value,
    searchQuery: searchQuery.value,
  }),
);
</script>

<style scoped>
.staff-documents {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0;
  padding: 24px;
}

.staff-documents__header {
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 20px;
}

.staff-documents__title {
  color: #111827;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.2;
  margin: 0;
}

.staff-documents__tabs {
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  margin-bottom: 20px;
}

.staff-documents__tab {
  background: transparent;
  border: 0;
  color: #6b7280;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: -1px;
  padding: 0 0 12px;
  position: relative;
}

.staff-documents__tab--active {
  color: #111827;
  font-weight: 600;
}

.staff-documents__tab--active::after {
  background: #111827;
  bottom: 0;
  content: '';
  height: 2px;
  left: 0;
  position: absolute;
  right: 0;
}

.staff-documents__search {
  align-items: center;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  display: flex;
  gap: 10px;
  margin-bottom: 8px;
  padding: 10px 14px;
}

.staff-documents__search-icon {
  color: #9ca3af;
  flex: none;
}

.staff-documents__search-input {
  border: 0;
  color: #111827;
  flex: 1;
  font-size: 15px;
  line-height: 1.4;
  min-width: 0;
  outline: none;
}

.staff-documents__search-input::placeholder {
  color: #9ca3af;
}

.staff-documents__list {
  display: flex;
  flex-direction: column;
}

.staff-documents__row--bordered {
  border-top: 1px solid #eef0f3;
}

.staff-documents__empty {
  color: #6b7280;
  font-size: 15px;
  padding: 24px 0;
}
</style>
